/* ============================================================
   ONLINE REPORT · EOM EDITORIAL — downloadable PDF.
   Channel-separated & future-proof: the generator's ONLY input is a discovered
   channels[] list. Every heading / sheet name / waterfall label / contents row
   derives from channel.name — "Swiggy"/"Zomato" appear only in the alias map and
   the data, never in the render logic. Onboard a new aggregator and its section
   appears automatically, named after itself, with no code changes.
   Pairs with sales-report-export.css (shared base) + online-report-export.css.
   ============================================================ */
import {
  formatINR, num, pct, esc, buildBars, htmlItem, tableItem,
  mastheadHTML, downloadEditorialReport,
} from './editorialPdfEngine';
import http from '../services/httpClient';
import { getPreparedFor } from './businessIdentity';
import '../components/online-report-export.css';

export { formatINR };

/* ---------- §1 channel identity (the only place names are interpreted) ---------- */
const CHANNEL_ALIASES = {
  swiggy: 'swiggy', 'swiggy pop': 'swiggy', 'swiggy instamart': 'swiggy',
  zomato: 'zomato', 'zomato pro': 'zomato',
  magicpin: 'magicpin', ondc: 'ondc', eatsure: 'eatsure', dunzo: 'dunzo',
};
const OTHERS_KEYS = new Set(['others', 'manual', 'generic', 'online']);
const DISPLAY_NAMES = { swiggy: 'Swiggy', zomato: 'Zomato', magicpin: 'Magicpin', ondc: 'ONDC', eatsure: 'EatSure', dunzo: 'Dunzo', others: 'Others' };

export function normalizeChannelKey(raw) {
  const k = String(raw || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!k) return 'others';
  if (OTHERS_KEYS.has(k)) return 'others';
  return CHANNEL_ALIASES[k] || k;
}
const titleCase = (s) => String(s || '').trim().replace(/\b\w/g, c => c.toUpperCase());
const displayName = (key) => DISPLAY_NAMES[key] || titleCase(key) || 'Others';

/* Reconciliation tie: within ₹1 or 1% of the reference. */
const ties = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) <= Math.max(1, Math.abs(Number(b) || 0) * 0.01);

/* ---------- §5 fetch (single period contract, allSettled, master list) ---------- */
export async function fetchOnlineReportInput({ start, end, label }) {
  const qs = (start && end) ? `?startDate=${start}&endDate=${end}` : '';
  const dqs = (start && end) ? `?start_date=${start}&end_date=${end}` : '';
  const settled = await Promise.allSettled([
    http.get(`/online-orders/dashboard${qs}`),
    http.get(`/online-orders/item-wise-report${qs}`),
    http.get(`/online-orders/detail${dqs}`),
    http.get('/online-platforms'),
  ]);
  const val = (i) => (settled[i].status === 'fulfilled' ? settled[i].value : null);
  const dashRes = val(0), itemsRes = val(1), detailRes = val(2), platRes = val(3);

  const dash = (dashRes && dashRes.data) ? dashRes.data : (dashRes || {});
  const itemsPayload = (itemsRes && itemsRes.data) ? itemsRes.data : (itemsRes || {});
  const items = Array.isArray(itemsPayload.items) ? itemsPayload.items : [];
  const orders = Array.isArray(detailRes?.orders) ? detailRes.orders
    : (Array.isArray(detailRes?.data?.orders) ? detailRes.data.orders : []);
  const platforms = Array.isArray(platRes?.platforms) ? platRes.platforms
    : (Array.isArray(platRes?.data?.platforms) ? platRes.data.platforms : []);

  const preparedFor = await getPreparedFor();
  const generated = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });

  return {
    meta: {
      brand: 'INVEXIS', title: 'Online Report',
      period: label || '', periodShort: label || '',
      generated, preparedFor, generatedBy: 'Generated by Invexis',
      kicker: 'Aggregator Channel Performance', heroLine1: 'Online', heroLine2: 'Report',
      subtitle: 'Every online channel reported separately — from menu price through platform discounts and commission down to the cash that actually settles in the bank.',
    },
    platformBreakdown: dash.platformBreakdown || {},
    items,
    orders,
    platforms,
  };
}

/* ---------- §2/§3 derive one channel's economics from its raw sums ---------- */
function deriveChannel(c) {
  const gross = +c.grossSales || 0, disc = +c.discount || 0, comm = +c.commission || 0;
  const tax = +c.taxes || 0, cogs = +c.cogs || 0, waste = +c.wastage || 0;
  const netSales = c.netSales != null ? +c.netSales : gross - disc;
  const netPayout = netSales - comm - tax;
  const grossProfit = c.grossProfit != null ? +c.grossProfit : netSales - cogs - waste;
  const orders = +c.orders || 0;
  // Margin over the sales of the dishes the profit INCLUDES — a dish whose cost
  // is unknown is left out of the profit, so its sales leave the base too.
  const gpBase = c.gpMeasurableNet != null ? +c.gpMeasurableNet : netSales;
  return {
    ...c, grossSales: gross, discount: disc, commission: comm, taxes: tax, cogs, wastage: waste,
    netSales, netPayout, grossProfit, orders,
    gpMeasurableNet: gpBase,
    gpExcludes: +c.gpExcludes || 0,
    gpKnown: c.gpKnown !== false,
    margin: gpBase > 0 ? grossProfit / gpBase * 100 : 0,
    aov: orders > 0 ? netSales / orders : 0,
    discountPct: gross > 0 ? disc / gross * 100 : 0,
    commissionPct: gross > 0 ? comm / gross * 100 : 0,
  };
}

/* ---------- §1 channel discovery + §3 aggregation ----------
   Union the master platform list with the channels present in the data, merge by
   normalized key (accumulating, so aliases fold together), then derive economics
   and reconciliation flags. Master channels with zero orders are kept. */
export function buildChannels({ platformBreakdown, items, orders, platforms }) {
  const map = new Map();
  const ensure = (rawKey, masterName) => {
    const key = normalizeChannelKey(rawKey);
    if (!map.has(key)) {
      map.set(key, {
        key, name: masterName || displayName(key), platform_id: null, fromMaster: false,
        grossSales: 0, discount: 0, commission: 0, taxes: 0, orders: 0,
        _finalSales: 0, _hasFinal: false, _cogs: 0, _waste: 0, _gp: 0, _hasItems: false,
        _measurableNet: 0, _gpExcludes: 0,
        items: [], _daily: new Map(),
      });
    }
    const c = map.get(key);
    if (masterName) c.name = masterName; // master list wins for proper casing
    return c;
  };

  // 1) seed from the master platform list (kept even with zero orders)
  (platforms || []).forEach((p) => {
    const c = ensure(p.name, titleCase(p.name));
    c.fromMaster = true;
    if (p.platform_id != null) c.platform_id = p.platform_id;
  });

  // 2) channel money totals (accumulate so aliased keys merge)
  Object.entries(platformBreakdown || {}).forEach(([key, b]) => {
    const c = ensure(key);
    c.grossSales += +b.totalSales || 0;
    c.discount += +b.totalDiscount || 0;
    c.commission += +b.totalCommission || 0;
    c.orders += +b.orderCount || 0;
    if (b.finalSales != null) { c._finalSales += +b.finalSales || 0; c._hasFinal = true; }
  });

  // 3) per-channel items + cogs/wastage/gross-profit
  (items || []).forEach((it) => {
    const c = ensure(it.channel || it.platform || 'others');
    const netSales = Number(it.finalSales != null ? it.finalSales : it.totalSales) || 0;
    const gp = Number(it.grossProfit) || 0;
    c._cogs += Number(it.totalCOGS) || 0;
    c._waste += Number(it.wastageAmount != null ? it.wastageAmount : it.wastageCost) || 0;
    // A dish whose cost is unknown comes back with COGS 0, so its "profit" is
    // its whole revenue. The item table already leaves it out; so does this.
    if (it.grossProfitUnavailable) {
      c._gpExcludes += 1;
    } else {
      c._gp += gp;
      c._measurableNet += netSales;
    }
    c._hasItems = true;
    c.items.push({
      name: it.itemName || it.name,
      qty: (it.quantitySold != null ? Number(it.quantitySold) : (it.qtySold != null ? Number(it.qtySold) : null)),
      netSales, grossProfit: gp, approximate: Boolean(it.grossProfitUnavailable),
      // C10. Distinct from `approximate`: those cells dash because the cost is unknown,
      // these print a real figure whose cost partly came from an old purchase price. This
      // report exported Gross Profit with no way to tell the two apart.
      estimated: !it.grossProfitUnavailable && Boolean(it.cogsEstimated),
      estimatedCost: Number(it.estimatedCOGSAmount) || 0,
    });
  });

  // 4) per-channel daily (order-level; net payout prorated so it reconciles)
  (orders || []).forEach((o) => {
    const c = ensure(o.platform || o.platform_source || 'others');
    const date = o.order_date || o.date || '—';
    if (!c._daily.has(date)) c._daily.set(date, { date, orders: 0, grossSales: 0, discount: 0, netSales: 0 });
    const d = c._daily.get(date);
    d.orders += 1;
    d.grossSales += Number(o.total_amount) || 0;
    d.discount += Number(o.discount_amount) || 0;
    d.netSales += Number(o.final_amount) || 0;
  });

  return Array.from(map.values()).map((c) => {
    c.items.sort((a, b) => b.netSales - a.netSales);
    const netSales = c._hasFinal ? c._finalSales : (c.grossSales - c.discount);
    const derived = deriveChannel({
      key: c.key, name: c.name, orders: c.orders,
      // Gross on the SAME basis as net: realised sales plus the discount. The
      // menu-price total is a different price basis, and "Gross − Discount =
      // Net" did not add up on it (the screen fixed this identity already).
      grossSales: netSales + c.discount, discount: c.discount, commission: c.commission, netSales,
      cogs: c._cogs, wastage: c._waste, grossProfit: c._hasItems ? c._gp : null,
      gpMeasurableNet: c._hasItems ? c._measurableNet : null,
      gpExcludes: c._gpExcludes,
      // Unknown when every dish on the channel is uncosted.
      gpKnown: !c._hasItems || c._gpExcludes === 0 || c._measurableNet > 0,
      items: c.items,
    });
    const chNet = derived.netSales || 0;
    const chComm = derived.commission || 0;
    const daily = Array.from(c._daily.values())
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .map((d) => ({
        date: d.date, orders: d.orders, netSales: d.netSales, discount: d.discount,
        netPayout: d.netSales - (chNet > 0 ? chComm * (d.netSales / chNet) : 0),
      }));

    const itemNetSum = c.items.reduce((s, i) => s + (+i.netSales || 0), 0);
    const dailyNetSum = daily.reduce((s, d) => s + (+d.netSales || 0), 0);
    const hasActivity = derived.orders > 0 || derived.grossSales > 0 || c.items.length > 0;

    return {
      ...derived,
      platform_id: c.platform_id, fromMaster: c.fromMaster, hasActivity,
      daily, items: c.items,
      itemNetSum, dailyNetSum,
      // §3 reconciliation flags + §6 "commission not reported"
      itemsTie: c.items.length === 0 || ties(itemNetSum, derived.netSales),
      dailyTie: daily.length === 0 || ties(dailyNetSum, derived.netSales),
      commissionReported: !(derived.commission === 0 && derived.netSales > 0 && c.key !== 'others'),
    };
  });
}

/* ---------- per-channel flow builders ---------- */

function itemFlowItems(ch) {
  if (!ch.items || !ch.items.length) {
    return [htmlItem(`<div class="callout"><p class="k">Item Detail</p><p>Item-level breakdown not available for this channel.</p></div>`)];
  }
  let netT = 0, gpT = 0, measurable = 0;
  const approx = [];
  // C10: rows whose printed profit leans on an old purchase price, and how much of it does.
  const estimatedNames = [];
  let estimatedCost = 0;
  const rows = ch.items.map((it) => {
    const sales = +it.netSales || 0; netT += sales;
    if (it.approximate) {
      approx.push(it.name);
      return `<tr><td class="name">${esc(it.name)} <span class="approx">approx</span></td><td class="num">${it.qty != null ? num(it.qty) : '&mdash;'}</td><td class="num">${formatINR(sales)}</td><td class="num dash">&mdash;</td><td class="num dash">&mdash;</td></tr>`;
    }
    const gp = +it.grossProfit || 0; gpT += gp; measurable += sales;
    const mg = sales > 0 ? gp / sales * 100 : 0;
    if (it.estimated) { estimatedNames.push(it.name); estimatedCost += Number(it.estimatedCost) || 0; }
    const estTag = it.estimated ? ' <span class="approx">~ est</span>' : '';
    return `<tr><td class="name">${esc(it.name)}${estTag}</td><td class="num">${it.qty != null ? num(it.qty) : '&mdash;'}</td><td class="num">${formatINR(sales)}</td><td class="num">${it.estimated ? '~' : ''}${formatINR(gp)}</td><td class="num">${it.estimated ? '~' : ''}${pct(mg)}</td></tr>`;
  });
  const star = approx.length ? ' *' : '';
  const mgT = measurable > 0 ? gpT / measurable * 100 : 0;
  const tfoot = `<tr><td class="name">Total</td><td class="num">&mdash;</td><td class="num">${formatINR(netT)}</td><td class="num">${formatINR(gpT)}${star}</td><td class="num">${pct(mgT)}${star}</td></tr>`;
  const out = [
    htmlItem(`<div class="chart-title">Top Items &mdash; ${esc(ch.name)}</div>`),
    tableItem(`<tr><th>Item</th><th class="num">Qty</th><th class="num">Net Sales</th><th class="num">Gross Profit</th><th class="num">Margin</th></tr>`, rows, tfoot),
  ];
  if (approx.length) {
    const names = approx.map(n => `<strong>${esc(n)}</strong>`).join(' and ');
    out.push(htmlItem(`<p class="fnote">* Gross profit and margin exclude ${names} &mdash; an ingredient went into negative stock, so their cost can't be trusted (shown &ldquo;&mdash;&rdquo;). Their sales still count.</p>`));
  }
  // C10. In the document, because a forwarded PDF is read by people who never saw the
  // setting that produced it — and it states the SIZE, since "some of this is estimated"
  // gives an accountant nothing to weigh.
  if (estimatedNames.length) {
    const names = estimatedNames.map(n => `<strong>${esc(n)}</strong>`).join(', ');
    out.push(htmlItem(`<p class="fnote">~ Estimated. ${names} include ingredients priced at their last known purchase price because stock was unavailable and no confirmed closing-stock price exists. Estimated cost included: ${formatINR(estimatedCost)}.</p>`));
  }
  if (!ch.itemsTie) {
    out.push(htmlItem(`<p class="fnote"><strong>Flagged:</strong> item-wise Net Sales (${formatINR(ch.itemNetSum)}) don't tie to this channel's Net Sales (${formatINR(ch.netSales)}) — verify the item records for ${esc(ch.name)}.</p>`));
  }
  return out;
}

/* Gross profit cells: a dash when no dish on the channel has a known cost, a
   star when some are left out (explained by gpFootnote). */
function gpCell(c) {
  if (c.gpKnown === false) return '&mdash;';
  return `${formatINR(c.grossProfit)}${c.gpExcludes ? ' *' : ''}`;
}
function marginCell(c) {
  if (c.gpKnown === false) return '&mdash;';
  return `${pct(c.margin)}${c.gpExcludes ? ' *' : ''}`;
}
function gpFootnote(c) {
  if (c.gpKnown === false) {
    return `<p class="fnote">Gross profit can&rsquo;t be shown for ${esc(c.name)} &mdash; no item on this channel has a known cost.</p>`;
  }
  if (c.gpExcludes > 0) {
    const n = c.gpExcludes;
    return `<p class="fnote">* Gross profit and margin exclude ${n} item${n === 1 ? '' : 's'} whose cost isn&rsquo;t known; their sales still count in Net Sales.</p>`;
  }
  return '';
}

function detailFlow(c) {
  // §6 zero-order channel → a single empty-state line (still "kept" so the reader sees it was checked).
  if (!c.hasActivity) {
    return [htmlItem(`<div class="callout empty"><div class="big">No ${esc(c.name)} orders in this period.</div></div>`)];
  }
  // §3 economics waterfall (drop the tax step when taxes aren't tracked, so the bar is honest).
  const wfSteps = [
    { label: 'Gross Sales', value: c.grossSales, display: formatINR(c.grossSales) },
    { label: '− Discount', value: c.netSales, display: formatINR(c.netSales) },
    { label: '− Commission', value: c.netSales - c.commission, display: formatINR(c.netSales - c.commission) },
  ];
  if (c.taxes > 0) wfSteps.push({ label: '− Taxes', value: c.netPayout, display: formatINR(c.netPayout) });
  const wf = buildBars(wfSteps, 'max');

  const strip1 = `<div class="strip4">
      <div class="cell"><div class="l">Gross Sales</div><div class="v">${formatINR(c.grossSales)}</div></div>
      <div class="cell"><div class="l">Discount</div><div class="v">${formatINR(c.discount)}</div></div>
      <div class="cell"><div class="l">Net Sales</div><div class="v">${formatINR(c.netSales)}</div></div>
      <div class="cell accent"><div class="l">Net Payout</div><div class="v">${formatINR(c.netPayout)}</div></div>
    </div>`;
  const strip2 = `<div class="strip4">
      <div class="cell"><div class="l">Orders</div><div class="v">${num(c.orders)}</div></div>
      <div class="cell"><div class="l">Avg Order Value</div><div class="v">${formatINR(Math.round(c.aov))}</div></div>
      <div class="cell"><div class="l">Gross Profit</div><div class="v">${gpCell(c)}</div></div>
      <div class="cell"><div class="l">Margin</div><div class="v">${marginCell(c)}</div></div>
    </div>`;

  const out = [
    htmlItem(strip1),
    htmlItem(strip2),
  ];
  const gpNote = gpFootnote(c);
  if (gpNote) out.push(htmlItem(gpNote));
  out.push(
    htmlItem(`<div class="chart-title">Gross Sales &rarr; Bank (per ${formatINR(c.grossSales)} gross)</div>${wf}`),
    ...itemFlowItems(c),
  );
  if (!c.commissionReported) {
    out.push(htmlItem(`<p class="fnote"><strong>Note:</strong> commission was not reported for ${esc(c.name)} this period, so Net Payout is shown without an aggregator cut and may not be exact.</p>`));
  }
  return out;
}

function dailyFlow(c) {
  const t = c.daily.reduce((a, d) => ({ o: a.o + (+d.orders || 0), n: a.n + (+d.netSales || 0), dc: a.dc + (+d.discount || 0), p: a.p + (+d.netPayout || 0) }), { o: 0, n: 0, dc: 0, p: 0 });
  const rows = c.daily.map(d => `<tr><td class="name">${esc(d.date)}</td><td class="num">${num(d.orders)}</td><td class="num">${formatINR(d.netSales)}</td><td class="num">${formatINR(d.discount)}</td><td class="num">${formatINR(d.netPayout)}</td></tr>`);
  const tfoot = `<tr><td class="name">Total</td><td class="num">${num(t.o)}</td><td class="num">${formatINR(t.n)}</td><td class="num">${formatINR(t.dc)}</td><td class="num">${formatINR(t.p)}</td></tr>`;
  const recon = c.dailyTie
    ? `Daily Net Sales sum to ${formatINR(t.n)} and Net Payout to ${formatINR(t.p)} — matching the ${esc(c.name)} summary. If a channel's daily or item-wise totals don't tie back to its header, that section is flagged for review.`
    : `<strong>Flagged:</strong> daily Net Sales (${formatINR(t.n)}) don't tie to the ${esc(c.name)} Net Sales (${formatINR(c.netSales)}) — verify the day-level records for this channel.`;
  return [
    tableItem(`<tr><th>Date</th><th class="num">Orders</th><th class="num">Net Sales</th><th class="num">Discount</th><th class="num">Net Payout</th></tr>`, rows, tfoot),
    htmlItem(`<div class="callout"><p class="k">Reconciliation</p><p>${recon}</p></div>`),
  ];
}

/* §4.3 overview: comparison table (AOV, Discount %, Commission %) + channel-share bars. */
function overviewFlow(channels, tot, totMargin) {
  const rows = channels.map(c => `<tr>
      <td class="name">${esc(c.name)}</td>
      <td class="num">${num(c.orders)}</td>
      <td class="num">${formatINR(c.netSales)}</td>
      <td class="num">${c.orders > 0 ? formatINR(Math.round(c.aov)) : '&mdash;'}</td>
      <td class="num">${c.grossSales > 0 ? pct(c.discountPct) : '&mdash;'}</td>
      <td class="num">${c.grossSales > 0 ? pct(c.commissionPct) : '&mdash;'}</td>
      <td class="num">${formatINR(c.netPayout)}</td>
      <td class="num">${gpCell(c)}</td>
      <td class="num">${c.netSales > 0 ? marginCell(c) : '&mdash;'}</td></tr>`);
  const totAov = tot.orders > 0 ? tot.netSales / tot.orders : 0;
  // The total covers only what could be costed; when any channel's profit
  // leaves something out, the total is marked too and the page says why.
  const excluding = channels.filter((c) => c.gpKnown !== false && c.gpExcludes > 0);
  const unknown = channels.filter((c) => c.gpKnown === false && c.netSales > 0);
  const totMark = (excluding.length || unknown.length) ? ' *' : '';
  const notes = [
    excluding.length
      ? `* Gross profit and margin exclude items whose cost isn&rsquo;t known — ${excluding.map((c) => `${esc(c.name)}: ${c.gpExcludes} item${c.gpExcludes === 1 ? '' : 's'}`).join('; ')}. Their sales still count in Net Sales.`
      : null,
    unknown.length
      ? `${excluding.length ? '' : '* '}Gross profit can&rsquo;t be shown where no item on ${unknown.map((c) => esc(c.name)).join(', ')} has a known cost; ${unknown.length === 1 ? 'that channel is' : 'those channels are'} left out of the All Channels profit.`
      : null,
  ].filter(Boolean);
  const totDiscPct = tot.grossSales > 0 ? tot.discount / tot.grossSales * 100 : 0;
  const totCommPct = tot.grossSales > 0 ? tot.commission / tot.grossSales * 100 : 0;
  const tfoot = `<tr><td class="name">All Channels</td><td class="num">${num(tot.orders)}</td><td class="num">${formatINR(tot.netSales)}</td><td class="num">${formatINR(Math.round(totAov))}</td><td class="num">${pct(totDiscPct)}</td><td class="num">${pct(totCommPct)}</td><td class="num">${formatINR(tot.netPayout)}</td><td class="num">${formatINR(tot.grossProfit)}${totMark}</td><td class="num">${pct(totMargin)}${totMark}</td></tr>`;
  const activeForBars = channels.filter(c => c.netSales > 0);
  const shareBars = buildBars(activeForBars.map(c => ({ label: c.name, value: c.netSales, display: formatINR(c.netSales) })), 'max');
  return [
    tableItem(`<tr><th>Channel</th><th class="num">Orders</th><th class="num">Net Sales</th><th class="num">AOV</th><th class="num">Disc %</th><th class="num">Comm %</th><th class="num">Net Payout</th><th class="num">Gross Profit</th><th class="num">Margin</th></tr>`, rows, tfoot),
    ...(notes.length ? [htmlItem(`<p class="fnote">${notes.join(' ')}</p>`)] : []),
    htmlItem(`<div class="chart-title">Net Sales Share by Channel</div>${shareBars}`),
  ];
}

function buildSections(channels, tot, totMargin, activeCount) {
  const sections = [];
  if (activeCount > 1) {
    sections.push({
      kickerHTML: `00 &mdash; <b>All Channels</b>`,
      title: 'Channel Overview', titleSm: true, footRight: 'Overview',
      lede: 'Every online channel side by side — order value after discount (Net Sales), the aggregator\'s cut (Commission %), and what actually reached the bank (Net Payout).',
      flow: () => overviewFlow(channels, tot, totMargin),
    });
  }
  channels.forEach((c, i) => {
    const nn = String(i + 1).padStart(2, '0');
    const hasDaily = c.hasActivity && c.daily && c.daily.length;
    sections.push({
      kickerHTML: `Channel ${nn} &mdash; <b>${esc(c.name)}</b>`,
      title: c.name, titleSm: false, footRight: `${c.name}${hasDaily ? ' · 1 of 2' : ''}`,
      lede: c.hasActivity
        ? `From menu price to bank: how ${c.name} orders converted into settled cash and gross profit this period.`
        : `${c.name} is set up as a channel but had no orders in this period.`,
      flow: () => detailFlow(c),
    });
    if (hasDaily) {
      sections.push({
        kickerHTML: `Channel ${nn} &mdash; <b>${esc(c.name)} · Daily</b>`,
        title: `${c.name} — Daily Breakdown`, titleSm: true, footRight: `${c.name} · 2 of 2`,
        lede: `Day-by-day ${c.name} orders, net sales, discount funded, and cash settled.`,
        flow: () => dailyFlow(c),
      });
    }
  });
  return sections;
}

function coverHTML(m, tot) {
  return `
  <section class="page cover">
    <div class="cover-top"><div class="cover-brand">${esc(m.brand)}</div>
      <div class="cover-tag">${esc(m.title)}<br>Period · ${esc(m.period)}<br>Generated · ${esc(m.generated)}</div></div>
    <div class="cover-mid">
      <div class="cover-kicker">${esc(m.kicker || 'Aggregator Channel Performance')}</div>
      <h1 class="cover-hero">${esc(m.heroLine1 || 'Online')}<br>${esc(m.heroLine2 || 'Report')}</h1>
      <p class="cover-sub">${esc(m.subtitle || '')}</p>
      <div class="cover-strip">
        <div class="cell"><div class="l">Net Sales (all channels)</div><div class="v">${formatINR(tot.netSales)}</div></div>
        <div class="cell"><div class="l">Net Payout</div><div class="v">${formatINR(tot.netPayout)}</div></div>
        <div class="cell"><div class="l">Online Orders</div><div class="v">${num(tot.orders)}</div></div>
      </div>
    </div>
    <div class="cover-foot"><span>Prepared for <strong>${esc(m.preparedFor || '')}</strong></span><span>${esc(m.generatedBy || 'Generated by Invexis')}</span></div>
  </section>`;
}

function contentsHTML(m, channels, activeCount) {
  let pg = 3;
  const idxRows = [];
  if (activeCount > 1) {
    idxRows.push(`<div class="idx-row"><span class="idx-num">00</span><span class="idx-title">All Channels — Overview</span><span class="idx-leader"></span><span class="idx-page">${String(pg++).padStart(2, '0')}</span></div>`);
  }
  channels.forEach((c, i) => {
    const n = String(i + 1).padStart(2, '0');
    idxRows.push(`<div class="idx-row"><span class="idx-num">${n}</span><span class="idx-title">${esc(c.name)} — Channel Detail</span><span class="idx-leader"></span><span class="idx-page">${String(pg++).padStart(2, '0')}</span></div>`);
    if (c.hasActivity && c.daily && c.daily.length) idxRows.push(`<div class="idx-row"><span class="idx-num">${n}</span><span class="idx-title">${esc(c.name)} — Daily Breakdown</span><span class="idx-leader"></span><span class="idx-page">${String(pg++).padStart(2, '0')}</span></div>`);
  });
  return `
  <section class="page">
    <div class="masthead">${mastheadHTML(m)}</div>
    <p class="kicker">Contents</p><h2 class="sec-title">In This Report</h2><hr class="rule">
    <div class="idx">${idxRows.join('')}</div>
    <div class="callout"><p class="k">How Channels Are Chosen</p>
      <p>Channels are discovered from the data, not fixed in code — this report shows <span class="eq">${channels.map(c => esc(c.name)).join(', ')}</span>. Any new aggregator automatically gets its own detailed section, named after itself, with no changes required.</p></div>
    <div class="pfoot"><span>${esc(m.generatedBy || 'Generated by Invexis')}</span><span>Contents</span></div>
  </section>`;
}

async function runOnline(input, fileName, output) {
  const meta = input.meta || {};
  // Keep channels that had activity OR exist in the master list (zero-order ones
  // render as an empty-state line so the reader sees they were checked). Others last.
  const channels = buildChannels(input)
    .filter(c => c.hasActivity || c.fromMaster)
    .sort((a, b) => { if (a.key === 'others') return 1; if (b.key === 'others') return -1; return (b.netSales || 0) - (a.netSales || 0); });

  const activeChannels = channels.filter(c => c.hasActivity);

  // §6 no online orders at all → a single page.
  if (activeChannels.length === 0) {
    return downloadEditorialReport({
      meta,
      coverHTML: coverHTML(meta, { netSales: 0, netPayout: 0, orders: 0 }),
      contentsHTML: '',
      sections: [{
        kickerHTML: 'Online Report', title: 'No Online Orders', titleSm: false, footRight: '—', lede: '',
        flow: () => [htmlItem(`<div class="callout empty"><div class="big">No online orders in this period.</div></div>`)],
      }],
      data: {}, fileName, output,
    });
  }

  const tot = activeChannels.reduce((a, c) => ({
    orders: a.orders + c.orders, grossSales: a.grossSales + c.grossSales, discount: a.discount + c.discount,
    commission: a.commission + c.commission, netSales: a.netSales + c.netSales, netPayout: a.netPayout + c.netPayout,
    grossProfit: a.grossProfit + (c.gpKnown === false ? 0 : c.grossProfit),
    gpMeasurableNet: a.gpMeasurableNet + (c.gpKnown === false ? 0 : c.gpMeasurableNet),
  }), { orders: 0, grossSales: 0, discount: 0, commission: 0, netSales: 0, netPayout: 0, grossProfit: 0, gpMeasurableNet: 0 });
  const totMargin = tot.gpMeasurableNet > 0 ? tot.grossProfit / tot.gpMeasurableNet * 100 : 0;

  return downloadEditorialReport({
    meta,
    coverHTML: coverHTML(meta, tot),
    contentsHTML: contentsHTML(meta, channels, activeChannels.length),
    sections: buildSections(channels, tot, totMargin, activeChannels.length),
    data: {}, fileName, output,
  });
}

export function downloadEditorialOnlinePdf(input, fileName) { return runOnline(input, fileName, undefined); }
/** The per-channel and overview flows, as HTML items — exported for tests. */
export const onlinePdfFlows = { detailFlow, overviewFlow };
export function buildEditorialOnlinePdfBlob(input, fileName) { return runOnline(input, fileName, 'blob'); }

const onlineReportEditorialPdf = { formatINR, normalizeChannelKey, buildChannels, fetchOnlineReportInput, downloadEditorialOnlinePdf, buildEditorialOnlinePdfBlob };
export default onlineReportEditorialPdf;
